import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import domainValidator from 'utils/validators/domainValidator'
import tinValidator from 'utils/validators/tinValidator'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import LoginField from 'modules/Forms/Account/Create/subcomponents/LoginField'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import renderSelect from 'components/FormControls/select'
import { FETCH_NETWORK_SETTINGS } from 'modules/Services/types'
import { useDispatch, useSelector } from 'react-redux'
import formStyles from 'modules/Forms/common-form.module.scss'
import { numWord } from 'utils/numWord'
import i18n from 'i18next'
import { numberWithSpace } from 'utils/numberWithSpace'
import GridSpinner from 'components/UI/GridSpinner'

const BUDGET_MIN_VALUE = 100000

const AD_FORMATS = ['geo_context', 'display', 'display_navigators']

const PLACEMENT_MIN_PERIOD_MONTHS = 4

export const TwoGisSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    landing_page: Yup.string()
      .test('domainValidator', 'common::validation_messages.incorrect_url', domainValidator)
      .required('common::validation_messages.required'),
    promotion_goal: Yup.string().required('common::validation_messages.required'),
    budget: Yup.string()
      .test({
        name: 'min_max',
        test: function (value, ctx) {
          const { path } = this

          if (+value >= BUDGET_MIN_VALUE) {
            return true
          } else {
            return ctx.createError({
              path,
              message: i18n.t('services::forms.register_account.two-gis.fields.budget.errors.min', {
                number: numberWithSpace(BUDGET_MIN_VALUE),
              }),
            })
          }
        },
      })
      .required('common::validation_messages.required'),
    placement_period_months: Yup.string().required('common::validation_messages.required'),
    country: Yup.string().required('common::validation_messages.required'),
    cities: Yup.string().required('common::validation_messages.required'),
    target_audience: Yup.string().required('common::validation_messages.required'),
    ad_formats: Yup.array().required('common::validation_messages.required'),
    kpi: Yup.string().required('common::validation_messages.required'),
    advertiser_legal_entity: Yup.string().required('common::validation_messages.required'),
    tax_number: Yup.string()
      .test(
        'tinValidator',
        'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
        tinValidator,
      )
      .required('common::validation_messages.required'),
  })

let CreateTwoGisAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  networkType,
  form,
  networkId,
  autofill,
  array,
  multiLogins,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { settings = null } = useSelector((state) => ({
    settings: state['services/account-create'].settings,
  }))

  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)

      dispatch({
        type: FETCH_NETWORK_SETTINGS,
        payload: { networkId },
      })
    }
  }, [networkId])

  const { countries = null } = settings || {}

  return (
    <>
      <Typography variant={'Header-60'} className={'margin-bottom-16'}>
        {t('services::networks.' + networkType)}
      </Typography>
      <Typography variant={'Body-20'} color="text-secondary" className={'margin-bottom-16'}>
        {t('services::forms.register_account.form_subtitle')}
      </Typography>
      <form onSubmit={handleSubmit(submitAccountData)} noValidate>
        <Field disabled name="network" hidden fullWidth component={renderInputField} t={t} />
        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.two-gis.fields.login.label'}
          />
        </div>
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="promotion_goal"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.promotion_goal.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.promotion_goal.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="budget"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.budget.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.budget.tooltip', {
                number: numberWithSpace(BUDGET_MIN_VALUE),
              })}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="placement_period_months"
            fullWidth
            component={renderSelect}
            options={Array(13 - PLACEMENT_MIN_PERIOD_MONTHS)
              .fill()
              .map((_, i) => ({
                value: i + PLACEMENT_MIN_PERIOD_MONTHS,
                label: `${i + PLACEMENT_MIN_PERIOD_MONTHS} ${numWord(
                  i + PLACEMENT_MIN_PERIOD_MONTHS,
                  [
                    t('services::forms.register_account.two-gis.month_1'),
                    t('services::forms.register_account.two-gis.month_2'),
                    t('services::forms.register_account.two-gis.month_5'),
                  ],
                )}`,
              }))}
            label={t(
              'services::forms.register_account.two-gis.fields.placement_period_months.label',
            )}
            t={t}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.two-gis.fields.placement_period_months.tooltip',
                { month: PLACEMENT_MIN_PERIOD_MONTHS },
              )}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="country"
            fullWidth
            component={renderSelect}
            options={
              countries?.map(({ id, name }) => ({
                value: id,
                label: name,
              })) ?? []
            }
            label={t('services::forms.register_account.two-gis.fields.country.label')}
            t={t}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
            readOnly={!countries}
            mode={'filter'}
            noResultsLabel={t('common::empty_results.header')}
            ControlProps={{
              startAdornment: !countries && <GridSpinner width={24} />,
            }}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.country.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16')}>
          <Field
            name="cities"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.cities.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="target_audience"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.target_audience.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.target_audience.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16')}>
          <Field
            name="ad_formats"
            fullWidth
            component={renderSelect}
            options={AD_FORMATS.map((id) => ({
              label: t(`services::forms.register_account.two-gis.fields.ad_formats.options.${id}`),
              value: id,
            }))}
            multiple
            label={t('services::forms.register_account.two-gis.fields.ad_formats.label')}
            t={t}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="kpi"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.kpi.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.kpi.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="advertiser_legal_entity"
            type="text"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.two-gis.fields.advertiser_legal_entity.label',
            )}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.two-gis.fields.advertiser_legal_entity.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="tax_number"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.tax_number.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.tax_number.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="landing_page"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.two-gis.fields.landing_page.label')}
            t={t}
            normalize={(value) => value.trim()}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.two-gis.fields.landing_page.tooltip')}
              placement="top"
              arrow
            >
              <Icon name={'information'} color={'secondary-grey'} size={'m'} />
            </Tooltip>
          </div>
        </div>

        <SubmitButtons submitting={submitting} onClose={onClose} />
      </form>
    </>
  )
}

CreateTwoGisAccountForm = reduxForm({
  form: 'CreateTwoGisAccountForm',
  initialValues: {
    login: '',
    promotion_goal: '',
    budget: '',
    placement_period_months: '',
    country: '',
    cities: '',
    target_audience: '',
    ad_formats: null,
    kpi: '',
    advertiser_legal_entity: '',
    tax_number: '',
    landing_page: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateTwoGisAccountForm)

CreateTwoGisAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
}

export default CreateTwoGisAccountForm
